import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Canonical from '../../seo/Canonical';
import './Logistics.css';
import Newsletter from '../Homepage/Newsletter';
import FAQs from '../Homepage/FAQs';
import '../Homepage/FAQ.css';
import { wmsIntegrationFaqs } from './wmsIntegrationFaqData';
import Footer from '../Layouts/Footer';
import LogisticsPageHero from './LogisticsPageHero';
import { useRevealClass } from '../../hooks/useInViewOnce';

const WMS_HERO_IMAGE = '/Images/wms-(2).jpg';

const WMSIntegrations: React.FC = () => {
  const servicesRevealRef = useRevealClass('logistics-services--visible');
  const whyRevealRef = useRevealClass('logistics-why--visible');

  const wmsServices: Array<{ id: number; title: string; copy: ReactNode }> = [
    {
      id: 1,
      title: 'Multi-Channel Order Sync',
      copy: (
        <>
          Connect your{' '}
          <Link to="/shopify-fulfillment-services" className="ff-inline-link">
            Shopify fulfilment
          </Link>
          , Amazon,{' '}
          <Link to="/tiktok-shop-fulfillment-services" className="ff-inline-link">
            TikTok Shop fulfilment
          </Link>
          , eBay and other ecommerce platforms directly to Mintsoft WMS for seamless order processing. Orders are
          imported automatically in real time, removing the need for manual CSV uploads, reducing errors and ensuring
          every order moves quickly from checkout to dispatch.
        </>
      ),
    },
    {
      id: 2,
      title: 'Live Inventory Updates',
      copy: 'Keep inventory accurate across every sales channel with real-time stock synchronisation. As products are received, picked or dispatched, stock levels update automatically, helping prevent overselling, improve inventory visibility and give customers confidence that what is available online is genuinely in stock.',
    },
    {
      id: 3,
      title: 'Custom API Integrations',
      copy: (
        <>
          Every business operates differently, which is why we offer bespoke API integrations tailored to your workflow.
          Whether you need EDI connections, webhooks, ERP integration, direct database synchronisation or custom software
          connectivity, our team can build a solution that fits — explore the wider stack on our{' '}
          <Link to="/integrations" className="ff-inline-link">
            ecommerce integrations
          </Link>{' '}
          page.
        </>
      ),
    },
    {
      id: 4,
      title: '24/7 Client Portal Access',
      copy: 'Stay in control with secure access to your dedicated client portal, available anytime and from any device. View live inventory, monitor inbound deliveries, track customer orders, manage returns, and access fulfilment reports through one easy-to-use dashboard.',
    },
  ];

  const wmsCapabilities = [
    {
      name: 'Order Sync Speed',
      volume: 'Orders sync within minutes',
      accuracy: 'Faster to dispatch',
    },
    {
      name: 'Supported Platforms',
      volume: 'Shopify, Amazon, TikTok Shop, eBay',
      accuracy: 'Plus custom APIs',
    },
    {
      name: 'Inventory Accuracy',
      volume: 'Bin-level stock visibility',
      accuracy: 'Fewer oversells',
    },
    {
      name: 'Portal Uptime',
      volume: 'Cloud Mintsoft dashboard',
      accuracy: 'Secure 24/7 access',
    },
  ];

  const wmsWorkflowSteps: Array<{ id: number; title: string; desc: ReactNode }> = [
    {
      id: 1,
      title: 'Connect Your Platforms',
      desc: 'Securely connect your Shopify, Amazon, TikTok Shop, eBay and other sales channels using API integrations and OAuth authentication.',
    },
    {
      id: 2,
      title: 'SKU Mapping & Testing',
      desc: (
        <>
          We map your products, variants and bundles, then complete comprehensive testing to ensure accurate order and
          inventory synchronisation — the same discipline covered in our{' '}
          <Link to="/blog/shopify-warehouse-integration" className="ff-inline-link">
            Shopify warehouse integration
          </Link>{' '}
          guide.
        </>
      ),
    },
    {
      id: 3,
      title: 'Go Live with Confidence',
      desc: 'Once approved, your integrations go live with real-time order syncing while our team monitors performance to ensure everything runs smoothly.',
    },
    {
      id: 4,
      title: 'Optimise & Scale',
      desc: (
        <>
          As your business grows, we refine workflows with custom automation, routing rules, inventory controls and
          international fulfilment capabilities — ideal for{' '}
          <Link to="/multi-channel-fulfillment-services" className="ff-inline-link">
            multi-channel fulfilment services
          </Link>
          .
        </>
      ),
    },
  ];

  return (
    <div className="logistics-page">
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />
      <LogisticsPageHero
        ariaLabel="WMS integrations"
        imageSrc={WMS_HERO_IMAGE}
        imageAlt="WMS integrations dashboard — real-time warehouse management"
        titleLight="WMS"
        titleAccent="Integrations"
        description={
          <p>
            Connect your ecommerce platforms with Mintsoft WMS for a faster, smarter fulfilment operation. Our seamless
            integrations automatically sync orders, inventory and shipping information in real time, helping you manage
            every sales channel from one central system. With complete visibility through your dedicated client portal,
            you&apos;ll always know what&apos;s in stock, what&apos;s been dispatched and what needs attention — accurate,
            efficient fulfilment from our{' '}
            <Link to="/fulfilment-centre-milton-keynes" className="ff-inline-link">
              Milton Keynes fulfilment centre
            </Link>
            .
          </p>
        }
        primaryCta={{ label: 'Get WMS Integration Quote', to: '/contact-fulfillment-services' }}
      />

      <section ref={servicesRevealRef} className="logistics-services" aria-labelledby="wms-services-title">
        <div className="logistics-services-inner">
          <header className="logistics-services-header">
            <p className="logistics-services-eyebrow">Powered by Mintsoft</p>
            <h2 id="wms-services-title" className="logistics-services-title">
              WMS Integration Services
            </h2>
          </header>
          <div className="logistics-tiles-grid" role="list">
            {wmsServices.map((service) => (
              <article key={service.id} className="logistics-tile logistics-tile-animate" role="listitem">
                <h3 className="logistics-tile-title">{service.title}</h3>
                <p className="logistics-tile-copy">{service.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '80px 0', background: '#ffffff' }}>
        <div className="container">
          <header style={{ textAlign: 'center', marginBottom: '50px' }}>
            <p className="logistics-services-eyebrow">Mintsoft Performance</p>
            <h2 className="logistics-services-title">Integration Capabilities</h2>
            <p style={{ maxWidth: '680px', margin: '14px auto 0', color: '#64748b', lineHeight: 1.65 }}>
              Seamlessly connect leading ecommerce platforms, marketplaces and business systems through powerful Mintsoft
              WMS integrations — then dig deeper with{' '}
              <Link to="/blog/amazon-shopify-ebay-tiktok-one-3pl-uk" className="ff-inline-link">
                Amazon, Shopify, eBay &amp; TikTok: one 3PL for all channels?
              </Link>
            </p>
          </header>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
            {wmsCapabilities.map((capability, index) => (
              <div
                key={index}
                style={{
                  padding: '30px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  textAlign: 'center',
                }}
              >
                <div style={{ color: '#5c82e9', fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '8px' }}>
                  {capability.name}
                </div>
                <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '4px' }}>{capability.volume}</div>
                <div style={{ fontSize: '0.85rem', color: '#1e3a8a', fontWeight: '600' }}>{capability.accuracy}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '100px 0', background: '#f8fafc' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <header style={{ marginBottom: '60px' }}>
            <p className="logistics-services-eyebrow">4 Step Process</p>
            <h2 className="logistics-services-title">WMS Integration Workflow</h2>
          </header>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '40px' }}>
            {wmsWorkflowSteps.map((step) => (
              <div key={step.id}>
                <div
                  style={{
                    width: '60px',
                    height: '60px',
                    background: '#1e3a8a',
                    color: '#fff',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                  }}
                >
                  {step.id}
                </div>
                <h3 style={{ color: '#0f172a', marginBottom: '12px' }}>{step.title}</h3>
                <p style={{ fontSize: '0.95rem', color: '#64748b' }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '80px 0', background: '#1e3a8a', color: '#fff' }}>
        <div
          className="container"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            alignItems: 'center',
            gap: '50px',
          }}
        >
          <div>
            <p style={{ color: '#fbbf24', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Mintsoft WMS
            </p>
            <h2 style={{ fontSize: '2.5rem', margin: '15px 0', lineHeight: '1.2' }}>
              The Technology Behind Your Fulfilment
            </h2>
            <p style={{ fontSize: '1.1rem', opacity: '0.9', lineHeight: '1.6' }}>
              Our fulfilment operation is powered by Mintsoft Warehouse Management System (WMS), a trusted cloud-based
              platform used by leading UK fulfilment providers. It connects seamlessly with your ecommerce stores and
              marketplaces, giving you real-time visibility of inventory, orders and warehouse activity through one
              intuitive dashboard. With native integrations and flexible APIs, Mintsoft helps automate your fulfilment
              process, improve accuracy and keep every sales channel synchronised as your business grows.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, marginTop: '20px' }}>
              {[
                'Native Shopify integration for automatic order and inventory synchronisation.',
                'Amazon SP-API integration with seamless order, inventory and fulfilment updates.',
                'TikTok Shop integration with real-time order processing and stock synchronisation.',
                'Real-time Stock API providing accurate inventory updates across all connected sales channels.',
              ].map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <span style={{ color: '#fbbf24', marginRight: '10px' }}>✓</span> {item}
                </li>
              ))}
            </ul>
          </div>
          <div
            style={{
              background: 'rgba(255,255,255,0.1)',
              padding: '40px',
              borderRadius: '20px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <h3 style={{ marginBottom: '20px' }}>One Integration. Every Sales Channel.</h3>
            <p style={{ fontSize: '0.95rem', opacity: '0.8', lineHeight: 1.6 }}>
              Keep your entire ecommerce operation connected through one intelligent fulfilment system. A single,
              unified inventory pool synchronises stock across all your sales channels in real time — the model behind{' '}
              <Link to="/blog/multi-channel-fulfilment-uk" className="ff-inline-link" style={{ color: '#fbbf24' }}>
                multi-channel fulfilment UK
              </Link>
              . With custom rules tailored to each platform, you can automate order routing, shipping preferences and
              inventory allocation while maintaining complete visibility and control.
            </p>
            <div
              style={{
                marginTop: '25px',
                padding: '15px',
                borderLeft: '4px solid #fbbf24',
                background: 'rgba(0,0,0,0.2)',
              }}
            >
              &quot;Our TikTok spikes sync perfectly now. No more manual stock adjustments across channels.&quot;
              <br />
              <em>— Multi-channel seller</em>
            </div>
          </div>
        </div>
      </section>

      <section ref={whyRevealRef} className="logistics-why">
        <div className="logistics-why-inner">
          <div className="logistics-why-top">
            <div className="logistics-why-copy">
              <p className="logistics-why-eyebrow">The Pick Pack Pro Difference</p>
              <h2 className="logistics-why-title">Why Choose Pick Pack Pro for WMS Integration?</h2>
              <p className="logistics-why-intro">
                A successful integration is about more than connecting software; it&apos;s about creating a fulfilment
                operation that runs efficiently every day. At Pick Pack Pro, our Mintsoft Warehouse Management System
                (WMS) works alongside experienced warehouse teams to provide real-time inventory visibility, seamless
                order processing and accurate fulfilment from goods-in to dispatch. With 99.9% order accuracy, scalable
                workflows and dedicated support, we help growing ecommerce brands streamline operations, reduce manual
                tasks and deliver a better experience for every customer. When you&apos;re shortlisting partners, read{' '}
                <Link to="/blog/how-to-choose-multi-channel-fulfilment-partner-uk-2026" className="ff-inline-link">
                  how to choose a multi-channel fulfilment partner UK
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      <FAQs
        title="WMS Integration FAQs"
        description="Get answers to the most common questions about WMS integrations, including supported ecommerce platforms, real-time inventory synchronisation, order management, API connectivity, onboarding and how Pick Pack Pro helps simplify multi-channel fulfilment from our Milton Keynes fulfilment centre."
        faqs={wmsIntegrationFaqs}
      />

      <Newsletter />
      <Footer />
    </div>
  );
};

export default WMSIntegrations;
